import type { QueryClient } from '@tanstack/react-query'

import { townsQuery } from '@/features/catalog/api'
import { useTownStore } from '@/features/town/store'
import { nearestTown } from '@/lib/geo'

import { locate } from './geolocate'
import { useLocationStore } from './store'

export type LocationPermission = PermissionState | 'unknown'

/** Browser permission for location, or 'unknown' where the Permissions API can't tell. */
async function permission(): Promise<LocationPermission> {
  try {
    const status = await navigator.permissions.query({ name: 'geolocation' })
    return status.state
  } catch {
    return 'unknown'
  }
}

/**
 * On app start, never prompting (D-05, D-65): when the browser already grants location, read it
 * quietly — so a shopper who allowed it once is never asked again — and let a town picked from the
 * location follow them (BR-24). A town chosen by hand is kept. A revoked permission forgets the
 * saved location. Returns the permission so the start-up prompt knows whether to offer it.
 */
export async function refreshLocation(qc: QueryClient): Promise<LocationPermission> {
  const { coords: before, optedOut } = useLocationStore.getState()
  const state = await permission()
  if (optedOut) return state
  if (state === 'denied') {
    if (before) useLocationStore.getState().setCoords(null)
    return state
  }
  if (state !== 'granted') return state
  try {
    const coords = await locate()
    useLocationStore.getState().setCoords(coords)
    const { auto, town, setTown } = useTownStore.getState()
    // A town picked from the location follows the shopper; a first quiet read fills "All towns".
    if (!auto && !(before === null && town === null)) return state
    const towns = await qc.ensureQueryData(townsQuery)
    setTown(nearestTown(coords, towns)?.slug ?? null, true)
  } catch {
    /* keep the last known location */
  }
  return state
}
